class UpcartAddonsDrawer extends HTMLElement {
  constructor() {
    super();
    this.isRequestPending = false;
    this.onToggleClick = this.onToggleClick.bind(this);
  }

  connectedCallback() {
    this.toggleButtonElement = this.querySelector('.upcart-addons-drawer__toggle');
    if (!this.toggleButtonElement) {
      return;
    }
    this.toggleButtonElement.addEventListener('click', this.onToggleClick);
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );
    this.autoAddInsuranceIfNeeded();
  }

  disconnectedCallback() {
    if (this.toggleButtonElement) {
      this.toggleButtonElement.removeEventListener('click', this.onToggleClick);
    }
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  get insuranceVariantId() {
    return Number(this.dataset.insuranceVariantId || 0);
  }

  get insuranceLineIndex() {
    return Number(this.dataset.insuranceLineIndex || 0);
  }

  get hasInsurance() {
    return this.dataset.hasInsurance === 'true';
  }

  set hasInsurance(newValue) {
    this.dataset.hasInsurance = newValue ? 'true' : 'false';
    if (this.toggleButtonElement) {
      this.toggleButtonElement.classList.toggle('is-active', newValue);
      this.toggleButtonElement.setAttribute('aria-checked', newValue ? 'true' : 'false');
    }
  }

  get sectionsToBundle() {
    const sectionIds = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionIds } })
    );
    return sectionIds;
  }

  cartHasInsurance(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return false;
    }
    return cart.items.some((item) => Number(item.variant_id) === this.insuranceVariantId);
  }

  cartHasNonInsuranceItem(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return false;
    }
    return cart.items.some((item) => Number(item.variant_id) !== this.insuranceVariantId);
  }

  get insuranceStorageKey() {
    return `upcart-insurance-preference-${this.insuranceVariantId}`;
  }

  getInsurancePreference() {
    try {
      const savedPreference = window.localStorage.getItem(this.insuranceStorageKey);
      if (!savedPreference) {
        return {
          optedOut: false,
          hasAutoAddedAtLeastOnce: false,
        };
      }
      const parsedPreference = JSON.parse(savedPreference);
      return {
        optedOut: Boolean(parsedPreference.optedOut),
        hasAutoAddedAtLeastOnce: Boolean(parsedPreference.hasAutoAddedAtLeastOnce),
      };
    } catch (error) {
      return {
        optedOut: false,
        hasAutoAddedAtLeastOnce: false,
      };
    }
  }

  saveInsurancePreference(updatedPreference) {
    try {
      window.localStorage.setItem(this.insuranceStorageKey, JSON.stringify(updatedPreference));
    } catch (error) {
      console.error(error);
    }
  }

  async autoAddInsuranceIfNeeded() {
    if (this.isRequestPending || !this.insuranceVariantId) {
      return;
    }

    try {
      const latestCartResponse = await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() });
      const latestCart = await latestCartResponse.json();

      if (this.cartHasInsurance(latestCart) && !this.cartHasNonInsuranceItem(latestCart)) {
        this.isRequestPending = true;
        this.toggleButtonElement.setAttribute('disabled', 'disabled');
        await this.removeInsuranceByVariantId();
        return;
      }

      if (this.hasInsurance) {
        return;
      }

      const insurancePreference = this.getInsurancePreference();
      if (insurancePreference.optedOut || insurancePreference.hasAutoAddedAtLeastOnce) {
        return;
      }

      if (!this.cartHasInsurance(latestCart) && this.cartHasNonInsuranceItem(latestCart)) {
        this.isRequestPending = true;
        this.toggleButtonElement.setAttribute('disabled', 'disabled');
        await this.addInsurance();
        this.saveInsurancePreference({
          optedOut: false,
          hasAutoAddedAtLeastOnce: true,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isRequestPending = false;
      this.toggleButtonElement.removeAttribute('disabled');
    }
  }

  async onCartUpdate(event) {
    if (!event || event.target === this || this.isRequestPending || !this.insuranceVariantId) {
      return;
    }

    const latestCart = event.cart;
    if (!latestCart || latestCart.errors) {
      return;
    }

    const hasInsuranceInCart = this.cartHasInsurance(latestCart);
    const hasNonInsuranceItemInCart = this.cartHasNonInsuranceItem(latestCart);

    if (hasInsuranceInCart && !hasNonInsuranceItemInCart) {
      this.isRequestPending = true;
      this.toggleButtonElement.setAttribute('disabled', 'disabled');
      try {
        await this.removeInsuranceByVariantId();
      } catch (error) {
        console.error(error);
      } finally {
        this.isRequestPending = false;
        this.toggleButtonElement.removeAttribute('disabled');
      }
      return;
    }

    const insurancePreference = this.getInsurancePreference();
    if (insurancePreference.optedOut || insurancePreference.hasAutoAddedAtLeastOnce) {
      return;
    }

    if (!hasInsuranceInCart && hasNonInsuranceItemInCart) {
      this.isRequestPending = true;
      this.toggleButtonElement.setAttribute('disabled', 'disabled');

      try {
        await this.addInsurance();
        this.saveInsurancePreference({
          optedOut: false,
          hasAutoAddedAtLeastOnce: true,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isRequestPending = false;
        this.toggleButtonElement.removeAttribute('disabled');
      }
    }
  }

  async onToggleClick() {
    if (this.isRequestPending || !this.insuranceVariantId) {
      return;
    }

    this.isRequestPending = true;
    this.toggleButtonElement.setAttribute('disabled', 'disabled');
    const hadInsuranceBeforeToggle = this.hasInsurance;

    // ✅ ADDED: Clarity tracking
    if (window.clarity) {
      window.clarity("event", hadInsuranceBeforeToggle
        ? "cart_warranty_remove"
        : "cart_warranty_add"
      );
    }

    try {
      if (hadInsuranceBeforeToggle) {
        this.hasInsurance = false;
        await this.removeInsurance();
        const insurancePreference = this.getInsurancePreference();
        this.saveInsurancePreference({
          ...insurancePreference,
          optedOut: true,
        });
      } else {
        this.hasInsurance = true;
        await this.addInsurance();
        const insurancePreference = this.getInsurancePreference();
        this.saveInsurancePreference({
          ...insurancePreference,
          optedOut: false,
        });
      }
    } catch (error) {
      this.hasInsurance = hadInsuranceBeforeToggle;
      console.error(error);
    } finally {
      this.isRequestPending = false;
      this.toggleButtonElement.removeAttribute('disabled');
    }
  }

  get insuranceProductTitle() {
    return this.dataset.insuranceProductTitle || 'Warranty';
  }

  async addInsurance() {
    // Use cart/add.js so we can attach line item properties for tracking.
    // cart/update.js does not support the "properties" field.
    const sectionsToBundle = this.sectionsToBundle;
    const payload = {
      id: this.insuranceVariantId,
      quantity: 1,
      properties: {
        '_source': 'HHD_WarrantyAdded_fromCartDrawer',
        '_product': this.insuranceProductTitle,
      },
      sections: sectionsToBundle,
      sections_url: window.location.pathname,
    };

    console.log('[HHD Warranty] 🛡️ addInsurance() called');
    console.log('[HHD Warranty] 📦 Payload:', JSON.stringify(payload, null, 2));

    const requestBody = JSON.stringify(payload);
    const addResponse = await fetch(FoxTheme.routes.cart_add_url, {
      ...FoxTheme.utils.fetchConfig(),
      body: requestBody,
    });
    const addResponseJson = await addResponse.json();

    console.log('[HHD Warranty] 📥 cart/add.js response status:', addResponse.status);
    console.log('[HHD Warranty] 📥 cart/add.js response body:', addResponseJson);

    if (addResponseJson.status) {
      console.error('[HHD Warranty] ❌ Error from cart/add.js:', addResponseJson.message);
      throw new Error(addResponseJson.message || 'Unable to add insurance item');
    }

    // cart/add.js returns the added item, not full cart. Fetch the full cart for pubsub.
    const cartJson = await (await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })).json();
    cartJson['sections'] = addResponseJson['sections'] || {};

    console.log('[HHD Warranty] ✅ Insurance added. Line item properties:', addResponseJson.properties);
    console.log('[HHD Warranty] 🛝 Cart now has', cartJson.item_count, 'items');

    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: cartJson,
      target: this,
      line: this.insuranceLineIndex,
      name: 'insurance',
    });
  }

  async removeInsurance() {
    if (!this.insuranceLineIndex) {
      return;
    }

    const requestBody = JSON.stringify({
      line: this.insuranceLineIndex,
      quantity: 0,
      sections: this.sectionsToBundle,
    });

    const removeResponse = await fetch(FoxTheme.routes.cart_change_url, {
      ...FoxTheme.utils.fetchConfig(),
      body: requestBody,
    });
    const removeResponseJson = await removeResponse.json();

    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: removeResponseJson,
      target: this,
      line: this.insuranceLineIndex,
      name: 'insurance',
    });
  }

  async removeInsuranceByVariantId() {
    const requestBody = JSON.stringify({
      updates: { [this.insuranceVariantId]: 0 },
      sections: this.sectionsToBundle,
      sections_url: window.location.pathname,
    });
    const removeResponse = await fetch(FoxTheme.routes.cart_update_url, {
      ...FoxTheme.utils.fetchConfig(),
      body: requestBody,
    });
    const removeResponseJson = await removeResponse.json();

    if (removeResponseJson.status) {
      throw new Error(removeResponseJson.message || 'Unable to remove insurance item');
    }

    this.hasInsurance = false;

    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: removeResponseJson,
      target: this,
      name: 'insurance',
    });
  }
}

customElements.define('upcart-addons-drawer', UpcartAddonsDrawer);
